import React, { useState, useEffect } from 'react';
import Joyride from 'react-joyride';
import { useUser } from '@clerk/clerk-react';

const FirstTimeUserTour = ({ isOpen, onClose }) => {
  const { user } = useUser();
  const [run, setRun] = useState(false);

  // Check if user has completed the tour before
  const hasCompletedTour = localStorage.getItem('hasCompletedCreateImageTour');
  
  useEffect(() => {
    // Only show tour if it's explicitly opened or if user hasn't completed it
    if (isOpen || (!hasCompletedTour && user)) {
      setRun(true);
    }
  }, [isOpen, hasCompletedTour, user]);

  const steps = [
    {
      target: '[data-tour="upload-section"]',
      content: (
        <div className="text-center">
          <h3 className="text-lg font-semibold text-white mb-2">Step 1: Upload Your Product Photo</h3>
          <p className="text-sm text-gray-300">
            Start by uploading a photo of your product. This will be used as a reference for generating your AI images.
          </p>
        </div>
      ),
      placement: 'top',
      disableBeacon: true,
      spotlightClicks: true,
    },
    {
      target: '[data-tour="creation-option"]',
      content: (
        <div className="text-center">
          <h3 className="text-lg font-semibold text-white mb-2">Step 2: Choose Creation Type</h3>
          <p className="text-sm text-gray-300">
            Select what you want to do with your product - create a new image, change the background, or remove the background.
          </p>
        </div>
      ),
      placement: 'top',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="product-selector"]',
      content: (
        <div className="text-center">
          <h3 className="text-lg font-semibold text-white mb-2">Step 3: Select Product Type</h3>
          <p className="text-sm text-gray-300">
            Choose the type of product you're working with (clothing, accessories, etc.) and specify the item.
          </p>
        </div>
      ),
      placement: 'top',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="style-selector"]',
      content: (
        <div className="text-center">
          <h3 className="text-lg font-semibold text-white mb-2">Step 4: Pick Image Style</h3>
          <p className="text-sm text-gray-300">
            Choose the visual style for your generated image - realistic, artistic, vintage, etc.
          </p>
        </div>
      ),
      placement: 'top',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="event-selector"]',
      content: (
        <div className="text-center">
          <h3 className="text-lg font-semibold text-white mb-2">Step 5: Choose Event (Optional)</h3>
          <p className="text-sm text-gray-300">
            Select an event or occasion to help the AI understand the context for your image.
          </p>
        </div>
      ),
      placement: 'top',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="model-selector"]',
      content: (
        <div className="text-center">
          <h3 className="text-lg font-semibold text-white mb-2">Step 6: Select Human Model</h3>
          <p className="text-sm text-gray-300">
            Choose a human model to wear or showcase your product in the generated image.
          </p>
        </div>
      ),
      placement: 'top',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="aspect-ratio"]',
      content: (
        <div className="text-center">
          <h3 className="text-lg font-semibold text-white mb-2">Step 7: Set Aspect Ratio</h3>
          <p className="text-sm text-gray-300">
            Choose the dimensions for your image - square for Instagram posts, portrait for stories, or landscape for banners.
          </p>
        </div>
      ),
      placement: 'top',
      spotlightClicks: true,
    },
    {
      target: '[data-tour="generate-button"]',
      content: (
        <div className="text-center">
          <h3 className="text-lg font-semibold text-white mb-2">Step 8: Generate Your Image</h3>
          <p className="text-sm text-gray-300">
            Once you've configured all settings, click Generate to create your AI-powered product image!
          </p>
        </div>
      ),
      placement: 'top',
      spotlightClicks: true,
    },
  ];

  const handleJoyrideCallback = (data) => {
    const { action, status, type } = data;
    
    if (action === 'close' || status === 'finished' || status === 'skipped') {
      setRun(false);
      localStorage.setItem('hasCompletedCreateImageTour', 'true');
      if (onClose) {
        onClose();
      }
    }
  };

  const joyrideProps = {
    steps,
    run,
    continuous: true,
    showProgress: true,
    showSkipButton: true,
    callback: handleJoyrideCallback,
    styles: {
      options: {
        primaryColor: '#8b5cf6', // Purple color matching the theme
        textColor: '#ffffff',
        backgroundColor: '#1f2937',
        overlayColor: 'rgba(0, 0, 0, 0.8)',
        arrowColor: '#1f2937',
        width: 320,
        zIndex: 10000,
      },
      tooltip: {
        borderRadius: 12,
        fontSize: 14,
        padding: 20,
      },
      tooltipContainer: {
        textAlign: 'center',
      },
      tooltipTitle: {
        fontSize: 16,
        fontWeight: 600,
        marginBottom: 8,
      },
      tooltipContent: {
        fontSize: 14,
        lineHeight: 1.4,
      },
      buttonNext: {
        backgroundColor: '#8b5cf6',
        borderRadius: 8,
        color: '#ffffff',
        fontSize: 14,
        fontWeight: 500,
        padding: '8px 16px',
      },
      buttonBack: {
        color: '#9ca3af',
        fontSize: 14,
        marginRight: 8,
      },
      buttonSkip: {
        color: '#9ca3af',
        fontSize: 14,
      },
      beacon: {
        inner: '#8b5cf6',
        outer: '#8b5cf6',
      },
    },
    locale: {
      back: 'Back',
      close: 'Close',
      last: 'Finish',
      next: 'Next',
      skip: 'Skip Tour',
    },
  };

  return <Joyride {...joyrideProps} />;
};

export default FirstTimeUserTour;
